/*
 * ===============================================================================================================
 *                                Copyright 2024, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import { getAllModuleAction } from '../module/moduleActionCreator';
import { SNACKBAR_ALERT_DEFAULT_PROPS } from '../../shared/constants';
import { setSnackBarDisplay } from '../snackBar/snackBarActionCreator';
import { getValidationErrorMessages } from '../../utility/commonUtility';
import { showErrorAction, clearErrorAction } from "../error/errorActionCreator";
import { hideLoaderAction, showLoaderAction } from "../loader/loaderActionCreator";
import { getAllApplicationAction, removeApplicationAction } from './applicationActionCreator';
import { createNewApplication, deleteApplication, getAllApplications, updateApplication } from '../../services/application';
import { ICreateApplicationRequest, IDeleteApplicationPayload, IEditApplicationRequest, IGetApplicationPayload } from '../../models/Organizations/Application';

export const getApplications = async (dispatch: Function, applicationPayload: IGetApplicationPayload) => {
  let applications: any = [];
  let selectedApplication: any = {};
  try {
    dispatch(showLoaderAction());
    dispatch(clearErrorAction());
    const applicationResponse = await getAllApplications(applicationPayload);
    if (applicationResponse.data.length) {
      applications = applicationResponse.data;
      selectedApplication = applications[0];
      if (applicationPayload.applicationId) {
        selectedApplication = applications.find((app: any) => app.id === applicationPayload.applicationId);
      }
      dispatch(getAllApplicationAction(applications, selectedApplication, applications.length > 0 ? false : true));
    } else {
      dispatch(getAllApplicationAction([], {}, true));
    }
    dispatch(hideLoaderAction());
  } catch (error) {
    const errorMessageDetail = getValidationErrorMessages(error);
    const errorMessage = {
      message: 'Error in fetching Application: ' + errorMessageDetail
    };
    applications = [];
    selectedApplication = {};
    dispatch(getAllApplicationAction(applications, selectedApplication, false));
    dispatch(showErrorAction(errorMessage));
    dispatch(hideLoaderAction());
    dispatch(
      setSnackBarDisplay({
        ...SNACKBAR_ALERT_DEFAULT_PROPS,
        show: true,
        severity: 'error',
        message: 'Error in fetching Application: ' + errorMessageDetail,
      })
    );
  }
};

export const createApplication = async (dispatch: Function, createAppPayload: ICreateApplicationRequest) => {
  try {
    dispatch(showLoaderAction("AddApplicationDialog"));
    dispatch(clearErrorAction());
    const createAppResponse = await createNewApplication(createAppPayload);
    if (createAppResponse.id) {
      getApplications(dispatch, {
        id: createAppPayload.organizationId,
        applicationId: createAppResponse.id,
        searchString: createAppPayload.searchString,
      });
    }
    dispatch(hideLoaderAction("AddApplicationDialog"));
    dispatch(
      setSnackBarDisplay({
        ...SNACKBAR_ALERT_DEFAULT_PROPS,
        show: true,
        severity: 'success',
        message: `Application ${createAppPayload.name} created successfully`,
      })
    );
    return true;
  } catch (error: any) {
    const errorMessageDetail = getValidationErrorMessages(error);
    const errorMessage = {
      message: `Error in creating Application: ` + errorMessageDetail
    };
    dispatch(showErrorAction(errorMessage));
    dispatch(hideLoaderAction("AddApplicationDialog"));
    dispatch(
      setSnackBarDisplay({
        ...SNACKBAR_ALERT_DEFAULT_PROPS,
        show: true,
        severity: 'error',
        message: 'Error in creating Application: ' + errorMessageDetail,
      })
    );
    return false;
  }
};

export const editApplication = async (dispatch: Function, editModulePayload: IEditApplicationRequest) => {
  try {
    dispatch(showLoaderAction("AddApplicationDialog"));
    dispatch(clearErrorAction());
    await updateApplication(editModulePayload);
    await getApplications(dispatch, {
      id: editModulePayload.organizationId,
      applicationId: editModulePayload.id,
      searchString: editModulePayload.searchString
    });
    dispatch(hideLoaderAction("AddApplicationDialog"));
    dispatch(
      setSnackBarDisplay({
        ...SNACKBAR_ALERT_DEFAULT_PROPS,
        show: true,
        severity: 'success',
        message: `Application ${editModulePayload.name} updated successfully`,
      })
    );
    return true;
  } catch (error: any) {
    const errorMessageDetail = getValidationErrorMessages(error);
    const errorMessage = {
      message: 'Error in Edit Application: ' + errorMessageDetail
    };
    dispatch(showErrorAction(errorMessage));
    dispatch(hideLoaderAction("AddApplicationDialog"));
    dispatch(
      setSnackBarDisplay({
        ...SNACKBAR_ALERT_DEFAULT_PROPS,
        show: true,
        severity: 'error',
        message: `Error in Edit Application: ${editModulePayload.name} ` + errorMessageDetail,
      })
    );
    return false;
  }
};

export const removeApplication = async (dispatch: Function, deleteApplicationPayload: IDeleteApplicationPayload) => {
  try {
    dispatch(showLoaderAction());
    dispatch(clearErrorAction());
    await deleteApplication(deleteApplicationPayload);
    await getApplications(dispatch, { id: deleteApplicationPayload.organizationId });
    dispatch(removeApplicationAction(deleteApplicationPayload.id));
    dispatch(hideLoaderAction());
    dispatch(
      setSnackBarDisplay({
        ...SNACKBAR_ALERT_DEFAULT_PROPS,
        show: true,
        severity: 'success',
        message: `Application ${deleteApplicationPayload.name} deleted successfully`,
      })
    );
  } catch (error: any) {
    const errorMessageDetail = getValidationErrorMessages(error);
    const errorMessage = {
      message: 'Error in Delete Application: ' + errorMessageDetail
    };
    dispatch(showErrorAction(errorMessage));
    dispatch(hideLoaderAction());
    dispatch(
      setSnackBarDisplay({
        ...SNACKBAR_ALERT_DEFAULT_PROPS,
        show: true,
        severity: 'error',
        message: `Error in Delete Application: ${deleteApplicationPayload.name} ` + errorMessageDetail,
      })
    );
  }
};

export const resetDataOnChangeOrganization = async (dispatch: Function) => {
  dispatch(getAllApplicationAction([], {}, true));
  dispatch(getAllModuleAction([], {}, {}, true));
};